import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  featureListService,
  featureDetailService,
  featureCreateService,
  featureUpdateService,
  featureListShortService,
  featureUpdateStatusService,
} from "./services";

type FeatureListResponse = Awaited<ReturnType<typeof featureListService>>;
type FeatureShortListResponse = Awaited<
  ReturnType<typeof featureListShortService>
>;
type FeatureDetailResponse = Awaited<ReturnType<typeof featureDetailService>>;

export const useFeatures = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof featureListService>[0];
  options?: QueryObserverOptions<unknown, unknown, FeatureListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "featureListHooks",
    queryFn: () => featureListService(params),
    ...options,
  });

  return response as UseQueryResult<FeatureListResponse>;
};

export const useShortFeatures = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof featureListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, FeatureShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "featureShortListHooks",
    queryFn: () => featureListShortService(params),
    ...options,
  });

  return response as UseQueryResult<FeatureShortListResponse>;
};

export const useFeatureDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof featureDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, FeatureDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "featureDetailHooks",
    queryFn: () => featureDetailService(params),
    ...options,
  });

  return response as UseQueryResult<FeatureDetailResponse>;
};

export const useFeatureUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    FeatureDetailResponse,
    unknown,
    Parameters<typeof featureUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "featureUpdateHooks",
    mutationFn: featureUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useFeatureCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    FeatureDetailResponse,
    unknown,
    Parameters<typeof featureCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "featureCreateHooks",
    mutationFn: featureCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useFeatureUpdateStatus = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    FeatureDetailResponse,
    unknown,
    Parameters<typeof featureUpdateStatusService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "featureUpdateStatusService",
    mutationFn: featureUpdateStatusService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
